// This MUI snackbar is required to wrap the Modus Alert component

import type { AlertProps } from "@mui/material";
import { Badge, IconButton } from "@mui/material";
import type { NotificationsProviderSlotProps } from "@toolpad/core";
import { Alert } from "@trimble/modus-cmx";
import { SnackbarTopLayer } from "@/components/app/SnackbarTopLayer";
import { Children, Fragment, isValidElement, type ReactNode } from "react";
import { flushSync } from "react-dom";

type BadgeChildProps = {
  badgeContent?: React.ReactNode;
  children?: React.ReactElement<AlertProps>;
};

/** Toolpad bundles optional action + MUI close; Modus dismiss replaces the close button. */
function getOptionalNotificationAction(action: ReactNode): ReactNode {
  if (!isValidElement<{ children?: ReactNode }>(action)) {
    return null;
  }

  const actionItems =
    action.type === Fragment
      ? Children.toArray(action.props.children)
      : [action];

  return (
    actionItems.find(
      (child) => isValidElement(child) && child.type !== IconButton
    ) ?? null
  );
}

const MuiModusSnackbar = (
  props: NotificationsProviderSlotProps["snackbar"]
) => {
  const { children, onClose, open, sx, ...rest } = props;

  if (!isValidElement<BadgeChildProps>(children)) {
    return (
      <SnackbarTopLayer {...rest} open={open} onClose={onClose} sx={sx}>
        {children}
      </SnackbarTopLayer>
    );
  }

  const alertElement = children.props.children;

  if (!isValidElement<AlertProps>(alertElement)) {
    return (
      <SnackbarTopLayer {...rest} open={open} onClose={onClose} sx={sx}>
        {children}
      </SnackbarTopLayer>
    );
  }

  const optionalAction = getOptionalNotificationAction(
    alertElement.props.action
  );

  const messageChild = alertElement.props.children;
  const alertTitle =
    typeof messageChild === "string" ? messageChild : undefined;
  const alertContent =
    typeof messageChild === "string" ? undefined : messageChild;

  const handleDismiss = (e: CustomEvent) => {
    // Modus removes the alert host after dismissClick; advance the queue first.
    flushSync(() => {
      onClose?.(e, "escapeKeyDown");
    });
  };

  return (
    <SnackbarTopLayer {...rest} open={open} onClose={onClose} sx={sx}>
      <Badge
        badgeContent={children.props.badgeContent}
        color="primary"
        sx={{ width: "100%" }}
      >
        <Alert
          alertTitle={alertTitle}
          dismissible
          onDismissClick={handleDismiss}
          variant={alertElement.props.severity}
          style={{ width: "100%" }}
        >
          <div slot="content">{alertContent}</div>
          {optionalAction ? <div slot="button">{optionalAction}</div> : null}
        </Alert>
      </Badge>
    </SnackbarTopLayer>
  );
};

export default MuiModusSnackbar;
